import { FieldType, ResourceDto, ResourceDtoPagination } from "@/orval/gen"
import {
  TheasoftColumnDef,
  SortingStateAndUpdater,
  InfiniteScrollingTable,
} from "@/routes/-components/custom-tanstack-table"
import { getFileType } from "@/utils/getFileType"
import { FieldData } from "@components/field-data"
import { useTranslation } from "react-i18next"
import { ResourceImage } from "./ResourceImage"
import { UseInfiniteQueryResult, InfiniteData } from "@tanstack/react-query"

interface Props {
  queryResult: UseInfiniteQueryResult<
    InfiniteData<ResourceDtoPagination, unknown>,
    Error
  >
  onClickItem: (item: ResourceDto) => void
  sortingState: SortingStateAndUpdater
  fieldTypes: FieldType[]
}

export function ResourcesTable({
  queryResult,
  onClickItem,
  sortingState,
  fieldTypes,
}: Props) {
  const { t } = useTranslation("app_server")
  const columns = React.useMemo<TheasoftColumnDef<ResourceDto>[]>(
    () => [
      {
        accessorKey: "resourceType",
        header: t("Type"),
      },
      {
        accessorKey: "name",
        header: t("Name"),
      },
      {
        accessorKey: "count",
        header: t("Amount"),
      },
      {
        accessorKey: "availableCount",
        header: t("Available"),
      },
      {
        accessorKey: "attachmentId",
        header: t("Picture"),
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const image = row.original.attachments.find(
            (a) => getFileType(a.fileName) === "image"
          )
          if (!image) return null
          return (
            <ResourceImage
              attachmentId={image.attachmentId}
              resourceId={row.original.resourceId}
              h={"100%"}
              w={"100%"}
              mah={100}
            />
          )
        },
      },
      {
        accessorKey: "location",
        header: t("Location"),
      },
      ...fieldTypes.map<TheasoftColumnDef<ResourceDto>>((type) => ({
        accessorKey: type.name,
        header: type.name,
        enableSorting: false,
        cell: (cell) => (
          <FieldData
            fieldData={
              cell.row.original.resourceFields
                ?.flatMap((rf) => rf.value)
                .find((field) => field?.type.id == type.id) ?? undefined
            }
          />
        ),
      })),
    ],
    [fieldTypes, t]
  )

  return (
    <InfiniteScrollingTable<ResourceDto>
      id="resources-table"
      infiniteQueryResult={queryResult}
      columnDefinitions={columns}
      sortingState={sortingState}
      getRowProps={(row) => ({
        onClick: () => onClickItem(row.original),
      })}
    />
  )
}

